// THE INBOUND-EMAIL ENVELOPE, AS THE CHAT READS IT (BUILD-DEC-155).
//
// An inbound reply is not an activity row — it is the TRIGGER that woke the run, so it arrives as an ordinary
// transcript turn whose text `comms` authored: one provenance line naming the sender, a blank line, then the
// person's own words. Rendered as prose that is a wall of quoted email pretending someone typed it; the owner
// wants it to read as a card in a thread. This module recognises the envelope and splits it back into its two
// server-authored halves so the surface can lay them out.
//
// NOTHING HERE IS AUTHORED (P-SELF). The sender name and the body are both bytes the server wrote; this file
// only locates the boundary between them. When the text is not the envelope it returns null and the turn
// renders as ordinary verbatim text — the honest default, never a card built from a guess.
//
// MIRRORED_CONTRACT: `INBOUND_EMAIL_*` in app/modules/comms/inbound-trigger-content.ts. The browser cannot
// import the Node-owned module at runtime, so these bytes are duplicated and pinned by the composition-level
// drift detector (chat-email-contract.unit.test.ts) — the same posture as CHAT_ACTIVITY_KINDS.

export const INBOUND_EMAIL_SENDER_UNRESOLVED = 'Someone on your team';
export const INBOUND_EMAIL_INTRO_SUFFIX = ' sent this to you by email:';
export const INBOUND_EMAIL_EMPTY_SUFFIX =
  ' replied to you by email, but the message carried no readable text.';
export const INBOUND_EMAIL_BODY_SEPARATOR = '\n\n';

/**
 * The longest a resolved display name can be before the surface stops believing the text is an envelope. A
 * real name is short; a long "sender" means some other prose happened to end in the same words, and claiming
 * THAT arrived by email would be a fabrication. Generous enough for a full name plus a company suffix.
 */
const MAX_SENDER_CHARS = 120;

/**
 * A platform id anywhere in a would-be sender name — MIRRORED from comms' own egress guard
 * (`INTERNAL_ID_SHAPE` in inbound-trigger-content.ts), for the same reason it exists there.
 *
 * WHY THE GUARD IS ON BOTH SIDES. Fixing the author was necessary but not sufficient: a turn is an IMMUTABLE
 * record (`recordHumanTurn`), so every conversation woken by email BEFORE the fix still holds the old
 * envelope with the raw `usr_…` id where the name now goes. Those rows are already written and will render
 * for as long as the conversation exists — and the card puts that string in the position a reader takes as
 * the sender's name, which is worse than the wall of text it replaced. The surface is the last boundary
 * before the owner's screen, so it re-applies the guard on what it READS (Agent.md §3.4, G-197).
 */
const INTERNAL_ID_SHAPE = /[a-z][a-z0-9]*_[0-9a-zA-Z]{12,}/;

/** One inbound email as the chat renders it: who wrote, and what they wrote. */
export interface InboundEmailTurn {
  /** The sender's display name as `comms` wrote it, or the neutral phrase — never an id (G-197). */
  readonly sender: string;
  /** The person's own words, verbatim. Null when the reply carried no readable text — stated, not implied. */
  readonly body: string | null;
}

/**
 * Recognise a transcript turn as an inbound email and split it. Returns null for anything that is not the
 * exact platform envelope, so ordinary human turns keep their ordinary rendering.
 *
 * The provenance line is always the FIRST line and the body always starts after exactly one blank line, so
 * the split is unambiguous even when the body itself contains blank lines (only the first separator is used).
 */
export function parseInboundEmailTurn(text: string): InboundEmailTurn | null {
  const separator = text.indexOf(INBOUND_EMAIL_BODY_SEPARATOR);
  const head = separator < 0 ? text : text.slice(0, separator);
  if (separator < 0 && head.endsWith(INBOUND_EMAIL_EMPTY_SUFFIX)) {
    const sender = senderOf(head, INBOUND_EMAIL_EMPTY_SUFFIX);
    return sender === null ? null : { sender, body: null };
  }
  if (separator < 0 || !head.endsWith(INBOUND_EMAIL_INTRO_SUFFIX)) return null;
  const sender = senderOf(head, INBOUND_EMAIL_INTRO_SUFFIX);
  if (sender === null) return null;
  const body = text.slice(separator + INBOUND_EMAIL_BODY_SEPARATOR.length);
  return { sender, body: body.trim() === '' ? null : body };
}

/**
 * The name in front of a known suffix, or null when there isn't a plausible one (see MAX_SENDER_CHARS).
 *
 * Three outcomes, and the difference matters. NULL means "this is not the envelope" and the turn renders as
 * ordinary prose — the honest default when the provenance is not the first line, so text that merely happens
 * to end in the same words cannot be dressed up as an email that arrived. The NEUTRAL PHRASE means "this IS
 * the envelope but the name in it is not one" — a turn recorded before comms resolved names. A name is
 * never invented and the id is never printed either way (P-SELF).
 */
function senderOf(head: string, suffix: string): string | null {
  const sender = head.slice(0, head.length - suffix.length).trim();
  if (sender === '' || sender.length > MAX_SENDER_CHARS || sender.includes('\n')) return null;
  return INTERNAL_ID_SHAPE.test(sender) ? INBOUND_EMAIL_SENDER_UNRESOLVED : sender;
}

/**
 * Collapse a consecutive run of EMAIL attempts down to the one that matters.
 *
 * The agent may attempt a send several times before one lands, and the trace records every attempt —
 * honestly, and it should. But the chat drew a full email CARD per attempt, so one delivered email read
 * as six identical boxes stacked in the thread (owner, 3 Aug 2026: "every email call is adding an email
 * box but only the final box is needed once the email is sent — makes the chat thread look really
 * messy"). One real send on the box produced seven cards.
 *
 * Keeps the SUCCESSFUL attempt where there is one, otherwise the last attempt, so a send that never
 * landed is still visible rather than tidied away — hiding a failed send is the one outcome worse than
 * a messy thread (Agent.md §3.8b). `refusedBefore` carries how many attempts preceded it, so the card
 * can say so instead of the count vanishing from the record.
 *
 * Only ADJACENT email rows collapse. Two sends separated by other work are two separate things a reader
 * needs to see, and merging them would misreport what happened.
 */
export interface CollapsedEmailRun<T> {
  readonly kept: T;
  readonly refusedBefore: number;
}

export function collapseEmailAttempts<T>(
  rows: readonly T[],
  isEmail: (row: T) => boolean,
  succeeded: (row: T) => boolean,
): readonly { readonly row: T; readonly refusedBefore: number }[] {
  const out: { readonly row: T; readonly refusedBefore: number }[] = [];
  let index = 0;
  while (index < rows.length) {
    const row = rows[index] as T;
    if (!isEmail(row)) {
      out.push({ row, refusedBefore: 0 });
      index += 1;
      continue;
    }
    let end = index;
    while (end + 1 < rows.length && isEmail(rows[end + 1] as T)) end += 1;
    const run = rows.slice(index, end + 1);
    const landed = run.find((candidate) => succeeded(candidate));
    const kept = landed ?? (run[run.length - 1] as T);
    out.push({ row: kept, refusedBefore: run.length - 1 });
    index = end + 1;
  }
  return out;
}
